import {
  createThunk,
  model,
  Model,
  getReferenceParentOfType,
  createNumberEntityRef,
} from 'mst-collection';
import { IAnyComplexType, types, Instance } from 'mobx-state-tree';
import { formatDistanceToNowStrict, parseJSON } from 'date-fns';

import { UserModel } from '../Viewer/UserModel';
import {
  getCommentApiByParentEntity,
  ThunkAbortController,
} from '../utils';
import { IUpdateCommentParams, LikeStatusEnum } from '@api';
import {
  CommentListModel,
  CommentList,
  CommentListParentEntityType,
} from './CommentListModel';
import { CommentUserStatsModel } from './CommentUserStatsModel';
import { trim } from '@utils';
import { CommentParentEntityEnum } from '@constants';

export class Comment extends Model({
  id: types.identifierNumber,
  userId: types.number,
  user: types.late(() => types.reference(UserModel)),
  message: types.string,
  sendTime: types.string,
  likes: types.number,
  userStats: CommentUserStatsModel,
  // FIXME: Here we have cycled types which are breaking typing
  comments: types.optional(
    types.late((): IAnyComplexType => CommentListModel),
    {},
  ),
}) {
  private get _CommentApi() {
    return getCommentApiByParentEntity(this.parentEntityName);
  }

  // FIXME: Here we have cycled types which are breaking typing
  private get _parent(): any {
    const parents = getReferenceParentOfType(this, CommentListModel);

    return parents?.find((parent) => parent.findById(this.id));
  }

  get parentEntityName(): CommentParentEntityEnum {
    return (this._parent as CommentList).parentEntityName;
  }

  get parentEntity(): CommentListParentEntityType {
    return (this._parent as CommentList).parentEntity;
  }

  /**
   * Id of the parent entity to which the comment belongs can be Post, Tutorial, Track, etc.
   */
  get parentEntityId(): number {
    return (this._parent as CommentList).parentEntityId;
  }

  /**
   * Id of the parent comment, if there is an id this means that this comment is a reply
   * to someone's comment
   */
  get parentCommentId(): number | undefined {
    return (this._parent as CommentList).parentCommentId;
  }

  get formattedSentTime() {
    return formatDistanceToNowStrict(parseJSON(this.sendTime), {
      addSuffix: true,
    });
  }

  get hasChildren(): boolean {
    return (this.comments as CommentList).totalCount > 0;
  }

  get canReply() {
    return !this.parentCommentId;
  }

  incrementLikes() {
    this.likes = this.likes + 1;
  }

  decrementLikes() {
    this.likes = this.likes - 1;
  }

  private _setMessage(value: string) {
    this.message = value;
  }

  private _toggleLikeAbortController = new ThunkAbortController();

  // TODO: find a way to make toggleLike reusable (we use the same logic in tutorial)
  toggleLike = createThunk(() => {
    return async function toggleLike(this: Comment) {
      try {
        // Cancel previous request if it in progress
        this._toggleLikeAbortController.abort();

        const newStatus = this.userStats.isLiked
          ? LikeStatusEnum.Unlike
          : LikeStatusEnum.Like;

        if (this.userStats.isLiked) {
          this.decrementLikes();
        } else {
          this.incrementLikes();
        }

        this.userStats.toggleIsLiked();

        await this._CommentApi.updateCommentLikeStatus(
          {
            id: this.parentEntityId,
            status: newStatus,
            commentId: this.id,
          },
          this._toggleLikeAbortController.create(),
        );

        this._toggleLikeAbortController.remove();
      } catch (error) {
        // If request was canceled do nothing
        if (ThunkAbortController.isCanceled(error)) {
          return;
        }

        // Change likes count and status back
        if (this.userStats.isLiked) {
          this.decrementLikes();
        } else {
          this.incrementLikes();
        }

        this.userStats.toggleIsLiked();

        throw error;
      }
    };
  });

  sendReport = createThunk((reasonTypeIds: number[]) => {
    return async function sendReport(this: Comment) {
      await this._CommentApi.sendCommentReport({
        id: this.parentEntityId,
        reasonTypeIds,
        commentId: this.id,
      });
    };
  });

  update = createThunk((params: IUpdateCommentParams) => {
    return async function update(this: Comment) {
      const oldMessage = this.message;

      try {
        const message = trim(params.message, true);

        this._setMessage(message);

        await this._CommentApi.updateComment({
          id: this.parentEntityId,
          commentId: this.id,
          message,
        });
      } catch (error) {
        // Set old message back
        this._setMessage(oldMessage);

        throw error;
      }
    };
  });

  remove = createThunk(() => {
    return async function remove(this: Comment) {
      await this._CommentApi.deleteComment({
        id: this.parentEntityId,
        commentId: this.id,
      });

      (this._parent as CommentList).removeItem(this);
    };
  });
}

export const CommentModel = model(Comment);

export const CommentRefModel = createNumberEntityRef(
  'comments',
  CommentModel,
);

export type CommentRefModelType = Instance<typeof CommentRefModel>;
